import { useCallback, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { CALENDAR_DATE_FORMAT } from '@/constants';
import {
  formatCalendarDateAsOptionValue,
  formatDate,
  parseNullishStringToDate,
} from '@/helpers/format-date';
import { CustomInputAsyncSelect } from '@/ui/date-picker/custom-input/custom-input-async-select';
import { DatePicker } from '@/ui/date-picker/date-picker';
import { getCustomSharedProps } from '@/ui/select/select-shared';
import { FormFieldError } from '@/components/form/form-field-error';
import { FormLabel } from './form-label';

interface Props<TForm extends FieldValues> {
  formContext: UseFormReturn<TForm>;
  fieldName: Path<TForm>;
  label: string;
  placeholder?: string;
  labelPosition?: 'top' | 'bottom';
}

export const FormSingleDatePicker = <TForm extends FieldValues>({
  formContext,
  fieldName,
  label,
  placeholder,
  labelPosition = 'bottom',
}: Props<TForm>) => {
  const {
    setValue,
    getFieldState,
    formState,
    getValues,
    formState: { defaultValues },
  } = formContext;

  const dateState = getFieldState(fieldName, formState);
  const [isOpen, setIsOpen] = useState(false);

  const { components, styles } = getCustomSharedProps({
    variant: 'small',
    withError: dateState.invalid,
    dropdownIndicatiorTransform: isOpen ? 'rotate3d(0, 0, 1, 180deg)' : 'unset',
  });

  const handleDateChange = useCallback(
    (newDate: Date | null) => {
      if (!newDate) {
        return;
      }
      // @ts-expect-error Typescript doesn't know the type of fieldName in generic form, we must make sure that fieldName is intended for date
      setValue(fieldName, formatCalendarDateAsOptionValue(newDate) ?? '');
      handleMenuClose();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultValues?.date],
  );

  const handleMenuClose = useCallback(() => {
    const date = getValues(fieldName);
    const formattedDefaultValue = defaultValues?.date
      ? formatDate(defaultValues?.date, {
          format: CALENDAR_DATE_FORMAT,
        })
      : '';

    const isDefaultValue = date === formattedDefaultValue;
    setValue(fieldName, date, {
      shouldDirty: !isDefaultValue,
      shouldValidate: true,
    });
    setIsOpen(false);
  }, [defaultValues?.date, getValues, setValue, fieldName]);

  return (
    <FormLabel name="upload-date" label={label} position={labelPosition}>
      <DatePicker
        id={fieldName}
        open={isOpen}
        selected={parseNullishStringToDate(getValues(fieldName))}
        onInputClick={() => setIsOpen(!isOpen)}
        onChange={handleDateChange}
        onCalendarClose={handleMenuClose}
        placeholderText={placeholder}
        customInput={
          <CustomInputAsyncSelect
            components={{ ...components }}
            styles={styles}
          />
        }
      />
      {dateState.invalid && (
        <FormFieldError absolute fontSize="12px" errorAlign="right">
          {dateState.error?.message}
        </FormFieldError>
      )}
    </FormLabel>
  );
};
